// The middle pane: one set of items shown as a grid of cards or as a list, with the same selection and the
// same keyboard model in both. Arrows move, Enter opens, F2 renames a folder, letters jump by name,
// Backspace goes up a level.
import {
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FolderItemRef } from "../folders.ts";
import { type Item, type Section, SHOT_STATE_LABEL } from "../items.ts";
import { formatRoute } from "../route.ts";
import { typeaheadKey, typeaheadMatch } from "../typeahead.ts";
import { useApp } from "./app.ts";
import { PeerDots } from "./collab/Avatars.tsx";
import { Icon } from "./icons.tsx";
import { Picture } from "./Picture.tsx";
import { RenameField } from "./RenameField.tsx";

export type ViewMode = "grid" | "list";
export const DRAG_MIME = "application/x-filmsquare-ref";

export interface DragPayload {
  ref?: FolderItemRef;
  folderId?: string;
}

export function setDrag(e: DragEvent, p: DragPayload, label: string) {
  e.dataTransfer.setData(DRAG_MIME, JSON.stringify(p));
  e.dataTransfer.setData("text/plain", label);
  e.dataTransfer.effectAllowed = "link";
}

export function readDrag(e: DragEvent): DragPayload | null {
  try {
    const raw = e.dataTransfer.getData(DRAG_MIME);
    return raw ? (JSON.parse(raw) as DragPayload) : null;
  } catch {
    return null;
  }
}

export const hasDrag = (e: DragEvent) => e.dataTransfer.types.includes(DRAG_MIME);

/**
 * Did a dragleave really leave the element? Crossing from the element onto one of its children also fires
 * dragleave (and not every browser fills in relatedTarget), so the pointer position decides.
 */
export function leftElement(e: DragEvent): boolean {
  const r = e.currentTarget.getBoundingClientRect();
  return e.clientX < r.left || e.clientX >= r.right || e.clientY < r.top || e.clientY >= r.bottom;
}

/** How a card got chosen: a single pointer click; anything else (keys, code) is left out. */
export type SelectHow = "click";

interface Props {
  items: Item[];
  mode: ViewMode;
  label: string;
  selected: string | null;
  /**
   * `how` is "click" for a single pointer click (or the focus a pointer press gives a card): the details panel waits
   * out a possible double-click then. A keyboard or code selection leaves it out, and the panel opens at once.
   */
  onSelect: (key: string | null, how?: SelectHow) => void;
  onOpen: (item: Item) => void;
  onUp?: () => void;
  renaming: string | null;
  onRenameStart: (item: Item) => void;
  onRename: (item: Item, name: string | null) => void;
  onDropOnFolder: (folderId: string, p: DragPayload) => void;
  /** may change folders (rename, take drops, be dragged); false for people below maker. Default true. */
  canEdit?: boolean;
  /** aspect class of the stills: "tall" for portrait projects (more columns) */
  aspect: "tall" | "wide";
  /** show contact strips on cards that have them (off by default: a still keeps the project's aspect) */
  strips?: boolean;
  empty: ReactNode;
  /** labelled groups of the items (scenes inside an episode); the items array stays flat and in the same order */
  sections?: Section[] | null;
  onOpenSection?: (s: Section) => void;
  /** focus the selected (or first) item when this number changes */
  focusSignal?: number;
  /** give focus back to this card without selecting it (the details panel closed); acts when `n` changes */
  restoreFocus?: { key: string; n: number } | null;
}

export function Browser(props: Props) {
  const { compact } = useApp();
  // In the grid, the small tiles (no still) sit together after the framed stills; the keyboard follows this order.
  const items = useMemo(
    () =>
      props.mode === "grid" && !props.sections
        ? [...props.items.filter((i) => !isCompact(i)), ...props.items.filter(isCompact)]
        : props.items,
    [props.items, props.mode, props.sections],
  );
  const p = { ...props, items };
  const canEdit = props.canEdit ?? true;
  const root = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const [dropKey, setDropKey] = useState<string | null>(null);

  const idx = p.items.findIndex((i) => i.key === p.selected);
  const tabKey = idx >= 0 ? p.selected : (p.items[0]?.key ?? null);

  const focusKey = (key: string) => {
    const el = root.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`);
    el?.focus();
    el?.scrollIntoView({ block: "nearest" });
  };

  // Focusing a card selects it, except a card that gets focus without being chosen: back from the details panel, or
  // the first card of a new place. It stays unselected (the panel stays closed) until an arrow or a click.
  const quietFocus = useRef<string | null>(null);

  // A request made before this grid was mounted is not for it (back on the overview, a request from the place we
  // left would focus the folders below the fold and scroll the overview away): only a new one counts.
  const mountSignal = useRef(p.focusSignal);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on an explicit request
  useEffect(() => {
    if (!p.focusSignal || !tabKey || p.focusSignal === mountSignal.current) return;
    mountSignal.current = undefined;
    if (idx < 0) quietFocus.current = tabKey;
    focusKey(tabKey);
    quietFocus.current = null;
  }, [p.focusSignal]);

  // Before the browser paints (so focus never rests on the page or the next control, like "Watch the cut"), and
  // once more on the next frame if the card was not there yet.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on an explicit request
  useLayoutEffect(() => {
    const r = p.restoreFocus;
    if (!r) return;
    const give = () => {
      quietFocus.current = r.key;
      focusKey(r.key);
      quietFocus.current = null;
      return document.activeElement?.getAttribute("data-key") === r.key;
    };
    if (give()) return;
    const frame = requestAnimationFrame(() => void give());
    return () => cancelAnimationFrame(frame);
  }, [p.restoreFocus?.n]);

  // A double click opens a card only when both clicks landed on it: the first click (event.detail 1) names the card,
  // and a second click that lands on another one (the layout moved in between) opens nothing.
  const firstClick = useRef<string | null>(null);
  // the card a pointer is pressing: the focus that press gives it is a click, not a keyboard move
  const pressed = useRef<string | null>(null);

  /**
   * The item above or below, read from the layout: the nearest row in that direction, and in it the item whose
   * centre is closest. Works across section headers and short last rows. In a list it is just the next row.
   */
  const vertical = (from: number, dir: 1 | -1) => {
    if (p.mode === "list") return from + dir;
    const els = [...(root.current?.querySelectorAll<HTMLElement>("[data-key]") ?? [])];
    const cur = els[from]?.getBoundingClientRect();
    if (!cur) return from;
    const cx = cur.left + cur.width / 2;
    let best = -1;
    let bestGap = Number.POSITIVE_INFINITY;
    let bestDx = Number.POSITIVE_INFINITY;
    els.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const gap = dir === 1 ? r.top - cur.bottom : cur.top - r.bottom;
      if (gap < -2) return;
      const dx = Math.abs(r.left + r.width / 2 - cx);
      if (gap < bestGap - 4 || (Math.abs(gap - bestGap) <= 4 && dx < bestDx)) {
        best = i;
        bestGap = gap;
        bestDx = dx;
      }
    });
    return best >= 0 ? best : from;
  };

  const move = (to: number) => {
    const i = Math.max(0, Math.min(p.items.length - 1, to));
    const it = p.items[i];
    if (!it) return;
    p.onSelect(it.key);
    focusKey(it.key);
  };

  const onKey = (e: KeyboardEvent, item: Item, i: number) => {
    if (p.renaming) return;
    const meta = e.metaKey || e.ctrlKey;
    // a card that has focus but is not chosen yet (see quietFocus): the first arrow chooses it, the next ones move
    const arrow =
      e.key === "ArrowUp" || e.key === "ArrowDown" || (p.mode === "grid" && e.key.startsWith("Arrow"));
    if (arrow && !meta && !e.altKey && p.selected !== item.key) {
      p.onSelect(item.key);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    switch (e.key) {
      case "ArrowRight":
        if (p.mode === "grid") move(i + 1);
        else return;
        break;
      case "ArrowLeft":
        if (p.mode === "grid") move(i - 1);
        else return;
        break;
      case "ArrowDown":
        if (meta) p.onOpen(item);
        else move(vertical(i, 1));
        break;
      case "ArrowUp":
        if (meta || e.altKey) p.onUp?.();
        else move(vertical(i, -1));
        break;
      case "Home":
        move(0);
        break;
      case "End":
        move(p.items.length - 1);
        break;
      case "Enter":
        p.onOpen(item);
        break;
      case "F2":
        if (item.folderId && canEdit) p.onRenameStart(item);
        break;
      case "Backspace":
        p.onUp?.();
        break;
      case "Escape":
        // deselects (the details panel closes); focus stays on the card, so the arrows go on working
        p.onSelect(null);
        break;
      default: {
        if (!typeaheadKey(e)) return;
        const byName = typeaheadMatch(
          typed.current,
          e.key,
          p.items.map((x) => [x.name, x.code ?? ""]),
        );
        if (byName >= 0) move(byName);
        break;
      }
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const renderAll = (render: (it: Item, i: number) => ReactNode) => {
    const secs = p.sections;
    if (!secs) return p.items.map(render);
    const at = new Map(p.items.map((it, i) => [it.key, i]));
    return secs.map((sec) => (
      <div key={sec.key} role="presentation" className="section">
        <div className="scene-head" role="presentation">
          {sec.code && <span className="code">{sec.code}</span>}
          {sec.open ? (
            <a
              href={formatRoute(sec.open)}
              className="section-title"
              onClick={(e) => {
                e.preventDefault();
                p.onOpenSection?.(sec);
              }}
            >
              {sec.title}
            </a>
          ) : (
            <span className="section-title">{sec.title}</span>
          )}
          <span className="muted">{sec.detail}</span>
        </div>
        {sec.itemKeys.length === 0 && <p className="section-empty muted">No shots in this scene yet.</p>}
        {sec.itemKeys.map((k) => {
          const i = at.get(k);
          return i === undefined ? null : render(p.items[i]!, i);
        })}
      </div>
    ));
  };

  if (p.items.length === 0 && !p.sections) return <div className="browser-empty">{p.empty}</div>;

  return (
    <div
      ref={root}
      className={`browser ${p.mode} ${p.aspect}`}
      role="listbox"
      aria-label={p.label}
      aria-orientation={p.mode === "list" ? "vertical" : undefined}
      onClick={(e) => e.target === e.currentTarget && p.onSelect(null)}
      onKeyDown={() => {}}
    >
      {p.mode === "list" && (
        <div className="list-head" aria-hidden="true">
          <span>Name</span>
          <span>Size</span>
          <span>State</span>
          <span className="num">Length</span>
          <span className="num">Contents</span>
          <span className="num">Cost</span>
        </div>
      )}
      {renderAll((it, i) => {
        const sel = it.key === p.selected;
        const renaming = it.key === p.renaming;
        return (
          <div
            key={it.key}
            data-key={it.key}
            role="option"
            aria-selected={sel}
            tabIndex={it.key === tabKey ? 0 : -1}
            style={it.ar ? ({ "--ar": it.ar } as CSSProperties) : undefined}
            className={`item k-${it.kind}${isCompact(it) && p.mode === "grid" ? " compact" : ""}${sel ? " sel" : ""}${dropKey === it.key ? " drop" : ""}${it.missing ? " missing" : ""}${it.loading ? " loading" : ""}`}
            draggable={!renaming && (!!it.ref || (!!it.folderId && canEdit))}
            onDragStart={(e) =>
              setDrag(e, it.folderId ? { folderId: it.folderId } : { ref: it.ref }, it.code ?? it.name)
            }
            onDragOver={(e) => {
              if (!it.folderId || !canEdit || !hasDrag(e)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "link";
              setDropKey(it.key);
            }}
            onDragLeave={(e) => leftElement(e) && setDropKey((k) => (k === it.key ? null : k))}
            onDrop={(e) => {
              setDropKey(null);
              const d = readDrag(e);
              if (it.folderId && canEdit && d) {
                e.preventDefault();
                p.onDropOnFolder(it.folderId, d);
              }
            }}
            onPointerDown={() => {
              pressed.current = it.key;
            }}
            onFocus={() =>
              !sel &&
              !compact &&
              quietFocus.current !== it.key &&
              p.onSelect(it.key, pressed.current === it.key ? "click" : undefined)
            }
            onClick={(e) => {
              pressed.current = null;
              if (compact) return p.onOpen(it);
              if (e.detail <= 1) firstClick.current = it.key;
              // a pointer click (detail 1, or 2 for the second click of a double-click); 0 is one made from the keyboard
              p.onSelect(it.key, e.detail >= 1 ? "click" : undefined);
            }}
            onDoubleClick={() => {
              // both clicks on this card; or the first click selected it and the list was drawn again in between
              // (a document's details panel), which loses the first click but not the selection
              if (firstClick.current === it.key || p.selected === it.key) p.onOpen(it);
              firstClick.current = null;
            }}
            onKeyDown={(e) => onKey(e, it, i)}
          >
            {p.mode === "grid" ? (
              <>
                {isCompact(it) ? (
                  <span className="compact-icon" aria-hidden="true">
                    <Icon name={it.glyph} size={20} />
                  </span>
                ) : it.kind === "folder" && it.strip && it.strip.length > 1 ? (
                  <div
                    className={`pic mosaic m${it.strip.length}`}
                    role="img"
                    aria-label={`${it.name}, first stills`}
                  >
                    {it.strip.map((t, k) => {
                      // a document in the folder shows as a file, not as one more empty folder
                      const g = it.stripGlyphs?.[k] ?? it.glyph;
                      return (
                        <Picture
                          key={t}
                          thumb={t}
                          alt=""
                          glyph={g}
                          className={g === "asset" ? "file-pic" : ""}
                          decorative
                        />
                      );
                    })}
                  </div>
                ) : p.strips && it.strip && it.strip.length > 1 ? (
                  <div className="pic strip3" role="img" aria-label={`${it.name}, first stills`}>
                    {it.strip.map((t) => (
                      <Picture key={t} thumb={t} alt="" glyph={it.glyph} decorative />
                    ))}
                  </div>
                ) : (
                  <Picture
                    thumb={it.thumb}
                    alt={it.code ?? it.name}
                    glyph={it.glyph}
                    className={it.kind === "folder" ? "folder-pic" : it.file && !it.thumb ? "file-pic" : ""}
                  />
                )}
                <div className="card-text">
                  {/* the title in words, then one mono line: code · detail · state (APP-SHELL-v2, rule 3) */}
                  <div className="card-title">
                    {renaming ? (
                      <RenameField value={it.name} onDone={(n) => p.onRename(it, n)} label="Folder name" />
                    ) : (
                      <span className="name">{it.name}</span>
                    )}
                  </div>
                  <div className="card-sub card-meta">
                    {it.missing ? (
                      <MissingTag label={it.detail} />
                    ) : (
                      <span className="detail">{cardMeta(it)}</span>
                    )}
                    <PeerDots itemKey={it.key} />
                  </div>
                  {it.note && <div className="card-note">{it.note}</div>}
                </div>
              </>
            ) : (
              <>
                <span className="cell name-cell">
                  <Picture thumb={it.thumb} alt="" glyph={it.glyph} className="mini" decorative />
                  {it.code && <span className="code">{it.code}</span>}
                  {renaming ? (
                    <RenameField value={it.name} onDone={(n) => p.onRename(it, n)} label="Folder name" />
                  ) : (
                    <span className="name">{it.name}</span>
                  )}
                  <PeerDots itemKey={it.key} />
                </span>
                <span className="cell mono muted">{it.kind === "shot" && it.size ? it.size : "–"}</span>
                <span className="cell">
                  {it.missing ? (
                    <MissingTag label={it.detail} />
                  ) : it.state ? (
                    <StateTag state={it.state} />
                  ) : (
                    <span className="muted">–</span>
                  )}
                </span>
                <span className="cell num mono">
                  {it.kind === "shot" || it.kind === "scene" ? it.facts[0] : ""}
                </span>
                <span className="cell num muted">
                  {it.kind === "shot" ? it.facts[1] : it.kind === "folder" ? it.detail : (it.facts[1] ?? "")}
                </span>
                <span className="cell num mono muted">
                  {it.kind === "shot" || it.kind === "episode" ? it.facts[2] : ""}
                </span>
              </>
            )}
            {it.folderId && !renaming && p.mode === "grid" && (
              <Icon name="folder" className="corner" size={14} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** An episode, scene or folder without a still: a small tile with an icon, not an empty frame. */
const isCompact = (it: Item): boolean =>
  (it.kind === "episode" || it.kind === "scene" || it.kind === "folder" || it.kind === "canvas") &&
  !it.thumb &&
  !it.strip?.length;

/** The second line of a card: a shot's size is a code (mono), the rest of its description is words. */
/** A card's one meta line, the plain word first so a narrow card never cuts it: "Picked · S1-B1-01 · ELS". Pure. */
export function cardMeta(it: Pick<Item, "code" | "detail" | "state">): string {
  return [it.state ? SHOT_STATE_LABEL[it.state] : null, it.code, it.detail].filter(Boolean).join(" · ");
}

export function CardDetail({ it }: { it: Item }) {
  const size = it.kind === "shot" ? it.size : null;
  if (size && it.detail.startsWith(size)) {
    return (
      <span className="detail">
        <span className="code">{size}</span>
        {it.detail.slice(size.length)}
      </span>
    );
  }
  return <span className="detail">{it.detail}</span>;
}

/** Archived or unavailable items say so in the status place: a neutral dot and a word. */
function MissingTag({ label }: { label: string }) {
  return (
    <span className="state st-archived">
      <i aria-hidden="true" />
      {label || "Archived"}
    </span>
  );
}

export function StateTag({ state }: { state: NonNullable<Item["state"]> }) {
  return (
    <span className={`state st-${state}`}>
      <i aria-hidden="true" />
      {SHOT_STATE_LABEL[state]}
    </span>
  );
}
